import { Injectable } from '@angular/core';

import { MingaRoleType } from 'libs/domain';
import { delta_pb, date_pb, legacy_pb, gateway } from 'libs/generated-grpc-web';
import { getRole } from 'libs/shared';
import { $enum } from 'ts-enum-util';

import { AuthService } from '@app/src/app/minimal/services/Auth';
import { StreamManager } from '@app/src/app/minimal/services/StreamManager';

export interface BasePersonFormFields {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  groups: Array<string>;
  fieldname: string | Array<string>;
  active: boolean;
  mingaHash?: string;
  pin: number;
  accountRoleType: string;
  displayName: string;
  birthdate: Date | null;
  studentId: string;
  grade: string;
  phoneNumber: string;
  customDisplayName: boolean;
  disabledDM: boolean;
  asbMember: boolean;
  idField1: string;
  idField2: string;
  parentEmail: string[];
  parentPhone: string[];
  enableParentNotification: boolean;
  sisInclude: boolean;
}

export interface PersonFormFields extends BasePersonFormFields {
  parentGroups: string[];
  idImageFilename: string;
}

export interface PersonUpdateFormFields extends Partial<BasePersonFormFields> {
  /**
   * Newly added parent groups
   */
  newParentGroups?: string[];

  /**
   * Parent groups being removed
   */
  removeParentGroups?: string[];
}

export namespace PersonFormFields {
  export function entries(fields: PersonFormFields) {
    return <[keyof PersonFormFields, any][]>Object.entries(fields);
  }

  export function defaultValue(): PersonFormFields {
    const defaultFormFields: PersonFormFields = {
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      active: true,
      groups: [],
      fieldname: '',
      pin: 0,
      accountRoleType: '',
      displayName: '',
      birthdate: null,
      studentId: '',
      grade: '',
      phoneNumber: '',
      customDisplayName: false,
      parentGroups: [],
      disabledDM: false,
      asbMember: false,
      idField1: '',
      idField2: '',
      parentEmail: [],
      parentPhone: [],
      idImageFilename: '',
      enableParentNotification: true,
      sisInclude: true,
    };

    return defaultFormFields;
  }
}

@Injectable({ providedIn: 'root' })
export class PeopleManagerService {
  constructor(
    private _peopleManager: gateway.people_ng_grpc_pb.PeopleManager,
    private _peopleAdmin: gateway.people_ng_grpc_pb.PeopleAdminManager,
    private _streamManager: StreamManager,
    private _authService: AuthService,
  ) {}

  async resendPinEmail(personHash: string, sendPinEmail: boolean) {
    const request = new gateway.people_pb.ResendPinEmailRequest();
    request.setPersonHash(personHash);
    request.setSendPinEmails(sendPinEmail);
    await this._peopleAdmin.resendPinEmail(request);
  }

  async createPeople(userProperties: PersonFormFields, sendPinEmail: boolean = false) {
    const request = new gateway.people_pb.CreatePeopleRequest();
    const person = new gateway.people_pb.Person();
    const personAccount = new gateway.people_pb.Account();
    const personIdentity = new gateway.people_pb.Identity();

    request.setSendPinEmails(sendPinEmail);

    person.setActive(userProperties.active);
    person.setParentGroupList(userProperties.parentGroups);

    if (userProperties.mingaHash) {
      const mingaUniqueHash = new legacy_pb.UniqueHash();
      mingaUniqueHash.setValue(userProperties.mingaHash);
      personAccount.setMingaHash(mingaUniqueHash);
    }

    personAccount.setDisplayName(userProperties.displayName);
    personAccount.setDisableParentNotification(!userProperties.enableParentNotification);

    personAccount.setSisInclude(userProperties.sisInclude);

    personIdentity.setEmailAddress(userProperties.email);
    personIdentity.setFirstName(userProperties.firstname);
    personIdentity.setLastName(userProperties.lastname);
    personIdentity.setPin(userProperties.pin);
    personIdentity.setStudentId(userProperties.studentId);
    personAccount.setIdField1(userProperties.idField1);
    personAccount.setIdField2(userProperties.idField2);
    personAccount.setGrade(userProperties.grade);
    personIdentity.setPhoneNumber(userProperties.phoneNumber);

    // @TODO: Hash this value over the network
    personIdentity.setPassword(userProperties.password);

    person.setAccountRoleType(userProperties.accountRoleType);

    person.setAccount(personAccount);
    person.setIdentity(personIdentity);
    if (userProperties.birthdate) {
      const dateMsg = new date_pb.Date();
      dateMsg.setYear(userProperties.birthdate.getUTCFullYear());
      dateMsg.setMonth(userProperties.birthdate.getUTCMonth());
      dateMsg.setDay(userProperties.birthdate.getUTCDate());

      person.setBirthdate(dateMsg);
    }

    if (userProperties.parentEmail) {
      personAccount.setParentEmailList(userProperties.parentEmail);
    }
    if (userProperties.parentPhone) {
      personAccount.setParentPhoneList(userProperties.parentPhone);
    }

    request.setPeopleList([person]);
    return await this._peopleManager
      .createPeople(request)
      .then(res => {
        if (res.getStatus() === legacy_pb.StatusCode.OK) {
          this._streamManager.restartStreamIfAvailable('AdminPeopleFeed');
        }
        return res;
      })
      .catch(err => {
        console.error('Create Person Error:', err);
        const status = legacy_pb.StatusCode.ERROR;
        const reason = new legacy_pb.StatusReason();
        const map = reason.getMessage();
        reason.setMessage(err.message);

        const result = new gateway.people_pb.CreatePeopleResponse();
        result.setStatus(status);
        result.addReason(reason);

        return result;
      });
  }

  async readPeople(userHashes: Array<string> = [], hiResAvatar: boolean = false, makeMinimal: boolean = false) {
    const req: gateway.people_pb.ReadPeopleRequest = new gateway.people_pb.ReadPeopleRequest();
    let res: gateway.people_pb.ReadPeopleResponse = new gateway.people_pb.ReadPeopleResponse();

    req.setHashList(userHashes);
    req.setHiResAvatar(hiResAvatar);

    try {
      res = await this._peopleManager.readPeople(req);
      let people: any[] = res.getPeopleList();
      if (makeMinimal) {
        people = people.map(p => {
          const identity = p.getIdentity();
          const account = p.getAccount();
          const roleType = $enum(MingaRoleType).asValueOrThrow(p.getAccountRoleType());
          const role = getRole(roleType);

          return {
            personHash: identity?.getHash(),
            profileImageUrl: account?.getProfileImageUrl(),
            displayName: account?.getDisplayName(),
            badgeIconColor: role.iconColor,
            badgeRoleName: role.name,
            badgeIconUrl: role.iconUrl,
            archived: p.getArchived(),
            firstName: identity.getFirstName(),
            lastName: identity.getLastName(),
            studentId: identity.getStudentId(),
          };
        });
      }

      return people;
    } catch (err) {
      console.warn('getPeople error', err);
      return [];
    }
  }

  async updatePerson(updateProperties: PersonUpdateFormFields, personHash: legacy_pb.UniqueHash) {
    const req: gateway.people_pb.UpdatePeopleRequest = new gateway.people_pb.UpdatePeopleRequest();
    let res: gateway.people_pb.UpdatePeopleResponse; // = new UpdatePeopleResponse();

    const personDelta = new gateway.people_pb.PersonDelta();
    const identityDelta = new gateway.people_pb.IdentityDelta();
    const accountDelta = new gateway.people_pb.AccountDelta();
    let dirtyIdentity = false;
    let dirtyAccount = false;

    personDelta.setHash(personHash.getValue());

    if (typeof updateProperties.email === 'string') {
      const delta = new delta_pb.StringDelta();
      delta.setNewString(updateProperties.email);
      identityDelta.setEmailAddress(delta);
      dirtyIdentity = true;
    }
    if (typeof updateProperties.password === 'string') {
      const delta = new delta_pb.StringDelta();
      delta.setNewString(updateProperties.password);
      identityDelta.setPassword(delta);
      dirtyIdentity = true;
    }
    if (typeof updateProperties.firstname === 'string') {
      const delta = new delta_pb.StringDelta();
      delta.setNewString(updateProperties.firstname);
      identityDelta.setFirstName(delta);
      dirtyIdentity = true;
    }
    if (typeof updateProperties.lastname === 'string') {
      const delta = new delta_pb.StringDelta();
      delta.setNewString(updateProperties.lastname);
      identityDelta.setLastName(delta);
      dirtyIdentity = true;
    }
    if (typeof updateProperties.active === 'boolean') {
      const delta = new delta_pb.BoolDelta();
      delta.setNewBool(updateProperties.active);
      personDelta.setActive(delta);
      dirtyIdentity = true;
    }
    if (typeof updateProperties.pin === 'number') {
      const delta = new delta_pb.Int32Delta();
      delta.setNewInt32(updateProperties.pin);
      identityDelta.setPin(delta);
      dirtyIdentity = true;
    }
    if (typeof updateProperties.studentId === 'string') {
      const delta = new delta_pb.StringDelta();
      delta.setNewString(updateProperties.studentId);
      identityDelta.setStudentId(delta);
      dirtyIdentity = true;
    }
    if (typeof updateProperties.grade === 'string') {
      const delta = new delta_pb.StringDelta();
      delta.setNewString(updateProperties.grade);
      accountDelta.setGrade(delta);
      dirtyAccount = true;
    }
    if (typeof updateProperties.phoneNumber === 'string') {
      const delta = new delta_pb.StringDelta();
      delta.setNewString(updateProperties.phoneNumber);
      identityDelta.setPhoneNumber(delta);
      dirtyIdentity = true;
    }
    if (typeof updateProperties.accountRoleType === 'string') {
      const delta = new delta_pb.StringDelta();
      delta.setNewString(updateProperties.accountRoleType);
      personDelta.setAccountRoleType(delta);
      dirtyIdentity = true;
    }
    if (typeof updateProperties.displayName === 'string') {
      const delta = new delta_pb.StringDelta();
      delta.setNewString(updateProperties.displayName);
      accountDelta.setDisplayName(delta);
      dirtyAccount = true;
    }
    if (typeof updateProperties.disabledDM === 'boolean') {
      const delta = new delta_pb.BoolDelta();
      delta.setNewBool(updateProperties.disabledDM);
      accountDelta.setDisabledDm(delta);
      dirtyAccount = true;
    }
    if (typeof updateProperties.asbMember === 'boolean') {
      const delta = new delta_pb.BoolDelta();
      delta.setNewBool(updateProperties.asbMember);
      personDelta.setAsbMember(delta);
    }
    if (typeof updateProperties.idField1 === 'string') {
      const delta = new delta_pb.StringDelta();
      delta.setNewString(updateProperties.idField1);
      accountDelta.setIdField1(delta);
      dirtyAccount = true;
    }
    if (typeof updateProperties.idField2 === 'string') {
      const delta = new delta_pb.StringDelta();
      delta.setNewString(updateProperties.idField2);
      accountDelta.setIdField2(delta);
      dirtyAccount = true;
    }
    if (typeof updateProperties.parentEmail === 'object') {
      if (updateProperties.parentEmail.length === 0) updateProperties.parentEmail = [''];
      for (const email of updateProperties.parentEmail) {
        const delta = new delta_pb.StringDelta();
        delta.setNewString(email);
        accountDelta.addParentEmail(delta);
      }
      dirtyAccount = true;
    }
    if (typeof updateProperties.parentPhone === 'object') {
      if (updateProperties.parentPhone.length === 0) updateProperties.parentPhone = [''];
      for (const phone of updateProperties.parentPhone) {
        const delta = new delta_pb.StringDelta();
        delta.setNewString(phone);
        accountDelta.addParentPhone(delta);
      }
      dirtyAccount = true;
    }

    if (typeof updateProperties.enableParentNotification === 'boolean') {
      const delta = new delta_pb.BoolDelta();
      delta.setNewBool(!updateProperties.enableParentNotification);
      accountDelta.setDisableParentNotification(delta);
      dirtyAccount = true;
    }

    if (typeof updateProperties.sisInclude === 'boolean') {
      const delta = new delta_pb.BoolDelta();
      delta.setNewBool(updateProperties.sisInclude);
      accountDelta.setSisInclude(delta);
      dirtyAccount = true;
    }

    if ('birthdate' in updateProperties) {
      const delta = new delta_pb.DateDelta();
      if (typeof updateProperties.birthdate === 'number') {
        const date = new Date(updateProperties.birthdate);
        const dateMsg = new date_pb.Date();
        dateMsg.setYear(date.getUTCFullYear());
        dateMsg.setMonth(date.getUTCMonth());
        dateMsg.setDay(date.getUTCDate());

        delta.setNewDate(dateMsg);
      } else if (updateProperties.birthdate) {
        const dateMsg = new date_pb.Date();
        dateMsg.setYear(updateProperties.birthdate.getUTCFullYear());
        dateMsg.setMonth(updateProperties.birthdate.getUTCMonth() + 1);
        dateMsg.setDay(updateProperties.birthdate.getUTCDate());

        delta.setNewDate(dateMsg);
      } else {
        delta.clearNewDate();
      }

      personDelta.setBirthdate(delta);
    }

    const parentGroupDelta = new delta_pb.RepeatedStringDelta();
    let hasParentGroupChanges = false;
    const { newParentGroups, removeParentGroups } = updateProperties;
    if (newParentGroups && newParentGroups.length > 0) {
      for (const newParentGroup of newParentGroups) {
        const msg = new delta_pb.RepeatedStringDelta.InsertInstruction();
        msg.setInsertString(newParentGroup);
        parentGroupDelta.addInsertInstruction(msg);
      }
      hasParentGroupChanges = true;
    }
    if (removeParentGroups && removeParentGroups.length > 0) {
      for (const rmvParentGroup of removeParentGroups) {
        const msg = new delta_pb.RepeatedStringDelta.EraseInstruction();
        msg.setEraseString(rmvParentGroup);
        parentGroupDelta.addEraseInstruction(msg);
      }
      hasParentGroupChanges = true;
    }

    if (hasParentGroupChanges) {
      personDelta.setParentGroup(parentGroupDelta);
    }

    if (dirtyIdentity) {
      personDelta.setIdentity(identityDelta);
    }
    if (dirtyAccount) {
      personDelta.setAccount(accountDelta);
    }

    req.setPeopleList([personDelta]);

    return await this._peopleManager.updatePeople(req).then(response => {
      if (response.getStatus() === legacy_pb.StatusCode.OK) {
        this._streamManager.restartStreamIfAvailable('AdminPeopleFeed');
      }
      return response;
    });
  }

  async openMinga(mingaHash: string) {
    const req: gateway.people_pb.OpenMingaRequest = new gateway.people_pb.OpenMingaRequest();
    console.log('[PeopleManagerService] openMinga() ', mingaHash);
    req.setMingaHash(mingaHash);

    return await this._peopleManager.openMinga(req);
  }

  async deletePerson(personHash: string) {
    const request = new gateway.people_pb.DeletePeopleRequest();
    console.log(`[PeopleManagerService] deletePerson() personHash: ${personHash}`);
    request.addHash(personHash);
    return await this._peopleManager.deletePeople(request);
  }

  async archivePerson(personHash: string) {
    const request = new gateway.people_pb.ArchivePeopleRequest();
    request.addHash(personHash);
    const people = await this._peopleManager.archivePeople(request);
    return people;
  }

  async selfArchivePerson(personHash: string) {
    const request = new gateway.people_pb.SelfArchivePersonRequest();
    await this._peopleManager.selfArchivePerson(request);
    await this._authService.logout();
  }

  async getMyTeams(personHash: string) {
    const request = new gateway.people_pb.GetPersonsTeamsRequest();
    request.setPersonHash(personHash);
    const response = await this._peopleManager.getTeams(request);
    return response.getTeamsList();
  }
}
